import { useEffect, useRef, useState } from 'react'
import { useUpdateEpisodeProgress } from '@/hooks/useEpisode'

interface YouTubePlayerProps {
  videoUrl: string
  episodeId: number
  initialProgress?: number
  onProgressUpdate?: (progress: number, completed: boolean) => void
}

// Tipos do YouTube IFrame API — apenas o subconjunto que usamos.
// Spec completa: https://developers.google.com/youtube/iframe_api_reference
interface YTPlayer {
  destroy: () => void
  getCurrentTime: () => number
  getDuration: () => number
}

interface YTPlayerEvent {
  target: YTPlayer
}

interface YTStateChangeEvent {
  data: number
}

interface YTPlayerOptions {
  videoId: string
  playerVars?: Record<string, number>
  events?: {
    onReady?: (event: YTPlayerEvent) => void
    onStateChange?: (event: YTStateChangeEvent) => void
  }
}

interface YTNamespace {
  Player: new (element: HTMLElement, options: YTPlayerOptions) => YTPlayer
}

declare global {
  interface Window {
    YT: YTNamespace
    onYouTubeIframeAPIReady: () => void
  }
}

export default function YouTubePlayer({
  videoUrl,
  episodeId,
  initialProgress = 0,
  onProgressUpdate,
}: YouTubePlayerProps) {
  const playerRef = useRef<YTPlayer | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [isReady, setIsReady] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [isPlaying, setIsPlaying] = useState(false)
  const lastSaveRef = useRef(0)
  const intervalRef = useRef<number | null>(null) // ← Armazena referência ao intervalo
  const updateProgressMutation = useUpdateEpisodeProgress()

  // Extrai o videoId da URL do YouTube (suporta múltiplos formatos)
  const getVideoId = (url: string) => {
    // Formato 1: https://www.youtube.com/watch?v=VIDEO_ID
    let match = url.match(/[?&]v=([^&]+)/)
    if (match) return match[1]

    // Formato 2: https://www.youtube.com/embed/VIDEO_ID
    match = url.match(/embed\/([^?]+)/)
    if (match) return match[1]

    // Formato 3: https://youtu.be/VIDEO_ID
    match = url.match(/youtu\.be\/([^?]+)/)
    if (match) return match[1]

    return null
  }

  const videoId = getVideoId(videoUrl)

  // Carrega a API do YouTube
  useEffect(() => {
    if (!videoId) return

    // Se já carregou a API, não carrega de novo
    if (window.YT && window.YT.Player) {
      initPlayer()
      return
    }

    // Carrega o script da API
    const tag = document.createElement('script')
    tag.src = 'https://www.youtube.com/iframe_api'
    const firstScriptTag = document.getElementsByTagName('script')[0]
    firstScriptTag.parentNode?.insertBefore(tag, firstScriptTag)

    // Callback quando a API estiver pronta
    window.onYouTubeIframeAPIReady = () => {
      initPlayer()
    }

    return () => {
      stopProgressTracking()
      if (playerRef.current) {
        playerRef.current.destroy()
      }
    }
  }, [videoId])

  // Cleanup quando o componente desmontar
  useEffect(() => {
    return () => {
      stopProgressTracking()
    }
  }, [])

  // Inicializa o player
  const initPlayer = () => {
    if (!containerRef.current || !videoId) return

    playerRef.current = new window.YT.Player(containerRef.current, {
      videoId,
      playerVars: {
        modestbranding: 1,
        rel: 0,
        showinfo: 0,
        start: Math.floor(initialProgress), // Começa de onde parou
      },
      events: {
        onReady: onPlayerReady,
        onStateChange: onPlayerStateChange,
      },
    })
  }

  // Player está pronto
  const onPlayerReady = (event: YTPlayerEvent) => {
    setIsReady(true)
    setDuration(event.target.getDuration())
  }

  // Estado do player mudou (play, pause, etc)
  const onPlayerStateChange = (event: YTStateChangeEvent) => {
    const state = event.data

    // 1 = playing, 2 = paused, 0 = ended
    if (state === 1) {
      setIsPlaying(true)
      startProgressTracking()
    } else if (state === 2) {
      setIsPlaying(false)
      stopProgressTracking()
      saveProgress() // Salva quando pausar
    } else if (state === 0) {
      // Vídeo terminou
      setIsPlaying(false)
      stopProgressTracking()
      markAsCompleted()
    }
  }

  // Inicia rastreamento de progresso
  const startProgressTracking = () => {
    // Limpa intervalo anterior se existir
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
    }

    // Cria novo intervalo
    intervalRef.current = window.setInterval(() => {
      // Verifica se o player existe e está realmente tocando
      if (playerRef.current) {
        try {
          const time = playerRef.current.getCurrentTime()
          const currentDuration = playerRef.current.getDuration()

          setCurrentTime(time)

          // Atualiza duration se mudou
          if (currentDuration > 0 && currentDuration !== duration) {
            setDuration(currentDuration)
          }

          // Salva a cada 10 segundos
          if (time - lastSaveRef.current >= 10) {
            saveProgress(time)
            lastSaveRef.current = time
          }

          // Auto-completa se assistir 90%+
          if (currentDuration > 0) {
            const watchedPercentage = (time / currentDuration) * 100
            if (watchedPercentage >= 90) {
              markAsCompleted()
            }
          }
        } catch (error) {
          console.error('Erro ao rastrear progresso:', error)
        }
      }
    }, 1000) // Verifica a cada 1 segundo
  }

  // Para o rastreamento de progresso
  const stopProgressTracking = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }

  // Salva progresso no backend
  const saveProgress = (time?: number) => {
    const watchTime = time || currentTime

    console.log('[YouTubePlayer] Saving progress:', {
      time,
      currentTime,
      watchTime,
      episodeId,
    })

    updateProgressMutation.mutate({
      episodeId,
      data: {
        watchedDuration: Math.floor(watchTime),
      },
    })

    onProgressUpdate?.(watchTime, false)
  }

  // Marca como completado
  const markAsCompleted = () => {
    // Pega a duração diretamente do player para garantir valor correto
    const currentDuration = playerRef.current?.getDuration() || duration
    const currentTime = playerRef.current?.getCurrentTime() || 0

    // Usa o maior valor entre currentTime e watchedDuration atual
    const finalDuration = Math.max(Math.floor(currentTime), Math.floor(currentDuration))

    console.log('[YouTubePlayer] Marking as completed:', {
      currentTime,
      currentDuration,
      finalDuration,
      episodeId,
    })

    updateProgressMutation.mutate({
      episodeId,
      data: {
        completed: true,
        watchedDuration: finalDuration,
      },
    })

    onProgressUpdate?.(finalDuration, true)
    stopProgressTracking() // Para o rastreamento quando completa
  }

  // Barra de progresso
  const progressPercentage = duration > 0 ? (currentTime / duration) * 100 : 0

  if (!videoId) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-muted">
        <p className="text-muted-foreground">URL de vídeo inválida</p>
      </div>
    )
  }

  return (
    <div className="relative w-full h-full">
      {/* Container do YouTube Player */}
      <div ref={containerRef} className="w-full h-full" />

      {/* Barra de progresso */}
      {isReady && (
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/30">
          <div
            className="h-full bg-primary transition-all duration-300"
            style={{ width: `${progressPercentage}%` }}
          />
        </div>
      )}

      {/* Indicador de salvamento */}
      {updateProgressMutation.isPending && (
        <div className="absolute top-4 right-4 bg-black/70 text-white text-xs px-3 py-1 rounded-full flex items-center gap-2">
          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
          Salvando progresso...
        </div>
      )}
    </div>
  )
}
